import type { KitVerifyClub } from '../api/kitAuth'

/**
 * Binding DISPOSITIVO → kit, persistido en localStorage. Misma key y mismo
 * shape que `vr-app/src/store/kitAccessStore.ts` (`versports.kitAccess`) a
 * propósito: la tablet del club y el Quest del mismo kit se leen igual, así
 * que un kit configurado de un lado se ve del otro.
 *
 * El código del kit ES la autenticación del DISPOSITIVO con el club (decisión
 * de mg, 2026-09-07): paso 1 en las dos superficies, y recién después el PIN
 * del jugador. Los estados y cuándo se vuelve a pedir viven en
 * `useKitBinding.ts` (web) y en `ui/KitLogin.tsx` (vr-app).
 *
 * NO es la sesión de auth: guarda sólo "a qué kit/club apunta este
 * dispositivo". El logout entre jugadores (`TabletLayout.endSession`) jamás lo
 * toca — el binding es del dispositivo, no del jugador.
 *
 * El web-app no usa zustand (sí el vr-app), así que en vez de un store esto
 * es un módulo con tres funciones y el mismo `safeLocalStorage` de allá:
 * private mode / quota pueden tirar excepción y nada de esto puede romper la
 * app.
 */

const STORAGE_KEY = 'versports.kitAccess'

export interface KitAccessClub {
  id: number
  name: string
  logoUrl: string | null
  primaryColor: string | null
  secondaryColor: string | null
  accentColor: string | null
}

export interface KitAccess {
  code: string
  club: KitAccessClub
  /**
   * ISO del último `/auth/kit/verify` exitoso. Es informativo (lo muestra el
   * pie de la pantalla de PIN para que el staff sepa desde cuándo está ligado
   * el dispositivo); nada gatea por él, así que un binding viejo de antes de
   * este campo se lee igual con `verifiedAt: null`.
   */
  verifiedAt: string | null
}

function safeLocalStorage(): Storage | null {
  try {
    return typeof localStorage !== 'undefined' ? localStorage : null
  } catch {
    return null
  }
}

function isKitAccessClub(v: unknown): v is KitAccessClub {
  return (
    !!v &&
    typeof v === 'object' &&
    typeof (v as KitAccessClub).id === 'number' &&
    typeof (v as KitAccessClub).name === 'string'
  )
}

export function loadKitAccess(): KitAccess | null {
  const ls = safeLocalStorage()
  if (!ls) return null
  try {
    const raw = ls.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as unknown
    if (!parsed || typeof parsed !== 'object') return null
    const { code, club, verifiedAt } = parsed as Partial<KitAccess>
    if (typeof code !== 'string' || !code || !isKitAccessClub(club)) return null
    return { code, club, verifiedAt: typeof verifiedAt === 'string' ? verifiedAt : null }
  } catch {
    return null
  }
}

export function saveKitAccess(code: string, club: KitAccessClub): KitAccess {
  const access: KitAccess = { code, club, verifiedAt: new Date().toISOString() }
  const ls = safeLocalStorage()
  if (ls) {
    try {
      ls.setItem(STORAGE_KEY, JSON.stringify(access))
    } catch {
      /* quota / private mode */
    }
  }
  return access
}

export function clearKitAccess(): void {
  const ls = safeLocalStorage()
  if (!ls) return
  try {
    ls.removeItem(STORAGE_KEY)
  } catch {
    /* quota / private mode */
  }
}

/** Normaliza el club que devuelve `/auth/kit/verify` al shape persistido. */
export function toKitAccessClub(c: KitVerifyClub): KitAccessClub {
  return {
    id: c.id,
    name: c.name,
    logoUrl: c.logo_url,
    primaryColor: c.primary_color,
    secondaryColor: c.secondary_color,
    accentColor: c.accent_color,
  }
}
